import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Gauge, Loader2, RefreshCw } from "lucide-react";

/** Configuración → Sistema: parámetros del stock mínimo automático según consumo. */
export default function StockMinimoAutoPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/productos/_/stock-minimo-auto").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/productos/_/stock-minimo-auto", { activo: cfg.activo, dias_analisis: Number(cfg.dias_analisis), lead_time_dias: Number(cfg.lead_time_dias), colchon_pct: Number(cfg.colchon_pct), piso: Number(cfg.piso) });
      toast.success(`Guardado · ${data.recalculo?.actualizados ?? 0} mínimos ajustados`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); } finally { setSaving(false); }
  };
  const recalcular = async () => {
    setSaving(true);
    try { const { data } = await api.post("/productos/_/stock-minimo-auto/recalcular"); toast.success(`${data.actualizados} mínimos ajustados de ${data.analizados}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Card className="p-5" data-testid="stock-minimo-auto-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-2"><Gauge className="w-4 h-4 text-primary" /> Stock mínimo automático</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            El sistema calcula el mínimo de cada producto según su consumo: <b>ventas por día × días de reposición + colchón</b>, nunca por debajo del piso. Se recalcula a diario. Si editas un mínimo a mano, ese producto queda "manual" y no se toca ({cfg.manuales} manual{cfg.manuales === 1 ? "" : "es"}).
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!cfg.activo} onChange={(e) => set("activo", e.target.checked)} className="w-4 h-4" data-testid="smin-activo" /> Activo</label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        {[["piso", "Piso (mínimo de mínimos)", "und"], ["lead_time_dias", "Días de reposición", "días"], ["dias_analisis", "Ventana de consumo", "días"], ["colchon_pct", "Colchón", "%"]].map(([k, l, u]) => (
          <div key={k}>
            <Label className="text-xs">{l}</Label>
            <div className="relative mt-1"><Input type="number" value={cfg[k]} onChange={(e) => set(k, e.target.value)} className="h-9 pr-10" data-testid={`smin-${k}`} /><span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">{u}</span></div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={guardar} disabled={saving} className="h-9" data-testid="smin-guardar">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar y recalcular"}</Button>
        <Button variant="outline" onClick={recalcular} disabled={saving} className="h-9" data-testid="smin-recalcular"><RefreshCw className="w-4 h-4 mr-1" /> Recalcular ahora</Button>
      </div>
    </Card>
  );
}
